import { useAppContext } from "@context/app";
import useParentSize from "@hooks/useParentSize";
import {
  IHomeItemDetailInput,
  IProductDetailModel,
  ISmallProductModel,
} from "@types";
import { convertStringNumber } from "@utils/digits";
import Link from "next/link";
import { useRouter } from "next/router";
import ImagePrepare from "../imagePrepare";
import { useTranslations } from "next-intl";
import { generateProductLink } from "./linkGenerator";

interface DynamicProductCardProps {
  product: IProductDetailModel | ISmallProductModel | IHomeItemDetailInput;
  searchAction?: () => void;
  width?: number;
  source?: string;
  sourceEntity?: string;
  sourceSlug?: string;
  section?: string;
  sectionEntity?: string;
  sectionSlug?: string;
  q?: string;
}

const DynamicProductCard: React.FC<DynamicProductCardProps> = ({
  product,
  searchAction,
  source,
  sourceEntity,
  sourceSlug,
  section,
  sectionEntity,
  sectionSlug,
  q,
}) => {
  const t = useTranslations()
  const { currency, decimal } = useAppContext();
  const { locale } = useRouter();
  const [size, parentRef] = useParentSize()
  const sku = product.skus?.find((sku) => sku.id === product.default_sku_id);
  if (!sku) return null;
  const href = generateProductLink(product, source, sourceSlug, sourceEntity, section, sectionEntity, sectionSlug, q);

  let priceInfo;
  if (sku.buy_limit) {
    const oldPriceFormatted = convertStringNumber(locale, sku.price.old_price?.toFixed(decimal));
    const priceFormatted = convertStringNumber(locale, sku.price.price.toFixed(decimal));

    if (sku.price.old_price) {
      priceInfo = (
        <div>
          <div dir="ltr" className="text-xs font-normal mt-2 flex space-x-1 flex-wrap items-center w-max">
            <span>{currency} </span>
            <s>{oldPriceFormatted}</s>
          </div>
          <div className="text-xs font-normal flex space-x-1 flex-wrap items-center w-max" dir="ltr">
            <span>{currency} </span>
            <span className="font-extrabold text-base text-black">{priceFormatted}</span>
          </div>
        </div>
      );
    } else {
      priceInfo = (
        <div className="text-xs text-black font-normal mt-2 flex space-x-1 flex-wrap items-center w-max" dir="ltr">
          <span>{currency} </span>
          <span className="font-extrabold text-base">{priceFormatted}</span>
        </div>
      );
    }
  } else {
    priceInfo = <p className="text-[#999] font-bold">{t("client_general_outofstock")}</p>;
  }

  const priceBadge = sku.buy_limit && sku.price.old_price ? (
    <div
      className="z-10 absolute top-3 right-3 w-7 h-7 rounded-lg flex justify-center items-center"
      style={{
        backgroundColor: sku.price.badge?.background_color || "#FFD600",
        color: sku.price.badge?.text_color,
      }}
    >
      <p className="font-bold text-[10px]">
        {convertStringNumber(locale, sku.price.badge?.text)}
      </p>
    </div>
  ) : null;

  return (
    <div className="w-full max-w-[190px] ">
      <Link
        href={href}
        passHref
        prefetch={false}
        onClick={searchAction}
        legacyBehavior>

        <div className="w-full h-max p-3 border rounded-xl relative text-[#333]">
          {priceBadge}
          <div className="w-full pt-[100%] relative">
            <div className="w-full h-full absolute top-0 left-0">
              <div className="w-full h-full rounded-lg overflow-hidden relative" ref={parentRef}>
                {sku.medias[0] ? <ImagePrepare media={sku.medias[0]} alt={product.name} width={size.width} /> : null}
              </div>
            </div>
          </div>
          <div className="w-full mt-5 bg-white flex flex-col justify-between flex-1 h-[90px]">
            <h2 className="font-normal text-sm line-clamp-2 whitespace-normal">
              {convertStringNumber(locale, product.name)}
            </h2>
            {priceInfo}
          </div>
        </div>

      </Link>
    </div>
  );
};

export default DynamicProductCard;
